"use client";

import type {
  EvaluatedFormula,
  FormulaReferenceDisplay,
} from "@atlas/computation";
import { useMemo, useState } from "react";

interface FamilyBreakdown {
  familyId: string;
  familyName: string;
  direction: "benefit" | "cost";
  monthlyValue: number;
  methodology?: string;
  detail?: {
    description?: string;
    expression?: string;
    readableExpression?: string;
    references?: FormulaReferenceDisplay[];
  };
}

interface ValueStreamWaterfallProps {
  families: FamilyBreakdown[];
  netAnnual: number;
  formulas?: EvaluatedFormula[];
  onStepOverride?: (
    familyId: string,
    stepId: string,
    value: number | null,
  ) => void;
}

function formatM(value: number): string {
  if (Math.abs(value) >= 1_000_000)
    return `$${(value / 1_000_000).toFixed(1)}M`;
  if (Math.abs(value) >= 1_000) return `$${(value / 1_000).toFixed(0)}K`;
  return `$${value.toFixed(0)}`;
}

interface BarData {
  familyId: string | null;
  label: string;
  value: number;
  direction: "benefit" | "cost" | "net";
}

/* -- Extracted row component so we can use hooks (useMemo) per row -- */

interface WaterfallBarRowProps {
  bar: BarData;
  maxValue: number;
  formula: EvaluatedFormula | undefined;
  detail?: FamilyBreakdown["detail"];
  isExpandable: boolean;
  isExpanded: boolean;
  onToggle: () => void;
  onStepOverride?: (
    familyId: string,
    stepId: string,
    value: number | null,
  ) => void;
}

function WaterfallBarRow({
  bar,
  maxValue,
  formula,
  detail,
  isExpandable,
  isExpanded,
  onToggle,
}: WaterfallBarRowProps) {
  const pct = (Math.abs(bar.value) / maxValue) * 100;

  const barColor =
    bar.direction === "benefit"
      ? "bg-emerald-500"
      : bar.direction === "cost"
        ? "bg-red-500"
        : "bg-blue-600";
  const labelColor =
    bar.direction === "benefit"
      ? "text-success-muted"
      : bar.direction === "cost"
        ? "text-danger-foreground"
        : "text-info-foreground";

  const affectedSteps = useMemo(() => {
    if (!formula) return new Set<string>();
    const affected = new Set<string>();
    const overriddenSteps = new Set(
      formula.steps
        .filter((s: EvaluatedFormula["steps"][number]) => s.isOverridden)
        .map((s: EvaluatedFormula["steps"][number]) => s.stepId),
    );
    for (const step of formula.steps) {
      for (const input of step.inputs) {
        if (input.source.type === "step") {
          if (
            overriddenSteps.has(input.source.stepId) ||
            affected.has(input.source.stepId)
          ) {
            affected.add(step.stepId);
            break;
          }
        }
      }
    }
    return affected;
  }, [formula]);

  void affectedSteps; // used by expanded formula steps when integrated

  const hasInlineDetail =
    !!detail &&
    !!(detail.description || detail.expression || detail.readableExpression);

  const rowContent = (
    <>
      <span className="w-4 flex-shrink-0 flex items-center justify-center">
        {isExpandable ? (
          <svg
            aria-hidden="true"
            className={`w-3.5 h-3.5 text-dim-foreground transition-transform ${isExpanded ? "rotate-90" : ""}`}
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={2}
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M8.25 4.5l7.5 7.5-7.5 7.5"
            />
          </svg>
        ) : (
          <span className="w-3.5" />
        )}
      </span>

      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          {bar.direction !== "net" && (
            <span
              className={`inline-flex items-center rounded-full px-1.5 py-0.5 text-[10px] font-medium ${
                bar.direction === "benefit"
                  ? "bg-green-100 text-green-700"
                  : "bg-red-100 text-red-700"
              }`}
            >
              {bar.direction === "benefit" ? "Benefit" : "Cost"}
            </span>
          )}
          <span className="min-w-0 break-words text-sm leading-5 text-secondary-foreground">
            {bar.label}
          </span>
        </div>
        <div className="mt-2 flex items-center gap-2">
          <div className="relative h-7 flex-1 overflow-hidden rounded bg-surface-alt">
            <div
              className={`absolute left-0 top-0 h-full rounded transition-all duration-300 ${barColor}`}
              style={{ width: `${Math.max(pct, 0.5)}%` }}
            />
          </div>
          <span
            className={`w-20 flex-shrink-0 text-right text-sm font-medium ${labelColor}`}
          >
            {bar.direction === "cost"
              ? `-${formatM(bar.value)}`
              : formatM(bar.value)}
          </span>
        </div>
      </div>
    </>
  );

  return (
    <div>
      {isExpandable ? (
        <button
          type="button"
          className="group flex w-full items-center gap-3 text-left"
          onClick={onToggle}
          aria-expanded={isExpanded}
        >
          {rowContent}
        </button>
      ) : (
        <div className="group flex items-center gap-3">{rowContent}</div>
      )}

      {/* Expanded inline detail */}
      {isExpanded && hasInlineDetail && (
        <div className="ml-8 mt-2 mb-1 space-y-2 border-l-2 border-border pl-4">
          {detail?.description && (
            <p className="text-xs text-secondary-foreground">
              {detail.description}
            </p>
          )}
          {(detail?.readableExpression ?? detail?.expression) && (
            <p className="text-xs text-muted-foreground font-mono">
              {detail?.readableExpression ?? detail?.expression}
            </p>
          )}
        </div>
      )}
    </div>
  );
}

/* -- Main waterfall component -- */

export function ValueStreamWaterfall({
  families,
  netAnnual,
  formulas,
  onStepOverride,
}: ValueStreamWaterfallProps) {
  const [expandedFamily, setExpandedFamily] = useState<string | null>(null);

  const bars: BarData[] = useMemo(() => {
    const items: BarData[] = families.map((f) => ({
      familyId: f.familyId,
      label: f.familyName,
      value: f.monthlyValue * 12,
      direction: f.direction,
    }));
    items.push({
      familyId: null,
      label: "Net Annual ROI",
      value: netAnnual,
      direction: "net",
    });
    return items;
  }, [families, netAnnual]);

  const formulaMap = useMemo(() => {
    if (!formulas) return new Map<string, EvaluatedFormula>();
    return new Map(formulas.map((f) => [f.familyId, f]));
  }, [formulas]);
  const detailMap = useMemo(
    () =>
      new Map(
        families.flatMap((family) => {
          const detail =
            family.detail ??
            (family.methodology
              ? {
                  description: family.methodology,
                }
              : undefined);

          if (
            !detail?.description &&
            !detail?.expression &&
            !detail?.readableExpression
          ) {
            return [];
          }

          return [[family.familyId, detail] as const];
        }),
      ),
    [families],
  );

  const maxValue = Math.max(...bars.map((b) => Math.abs(b.value)), 1);

  return (
    <div className="space-y-3">
      {bars.map((bar) => {
        const detail = bar.familyId ? detailMap.get(bar.familyId) : undefined;
        const isExpandable =
          bar.familyId !== null &&
          (formulaMap.has(bar.familyId) ||
            !!detail?.description ||
            !!detail?.expression ||
            !!detail?.readableExpression);
        const isExpanded = expandedFamily === bar.familyId;
        const formula = bar.familyId ? formulaMap.get(bar.familyId) : undefined;

        return (
          <WaterfallBarRow
            key={bar.label}
            bar={bar}
            maxValue={maxValue}
            formula={formula}
            detail={detail}
            isExpandable={isExpandable}
            isExpanded={isExpanded}
            onToggle={() => setExpandedFamily(isExpanded ? null : bar.familyId)}
            onStepOverride={onStepOverride}
          />
        );
      })}
    </div>
  );
}
